/* eslint-disable @typescript-eslint/no-explicit-any */
import { Instant, LocalDate, ZoneId } from 'js-joda';
import { TockProductLevel } from '@tock/shared/types/productFeatures';
import { TockProductType } from 'types/productFeatures';
import { cloneDeep } from 'es-toolkit/compat';
import { core, dashboard } from 'types';
import { safeToNumber } from '@tock/coretypes';
import { stringify } from '@tock/query-string';
import ClowncarAppConstants from 'constants/ClowncarAppConstants';
import protos from 'utils/protos';

const {
  Business,
  BusinessGroupInternal,
  BankAccountUpdateRequest,
  BusinessActivation,
  BusinessDetailsUpdateRequest,
  BusinessTypeRequest,
  InvoiceChangeRequest,
  PayoutConfigChangeRequest,
  SearchAttribute,
  Subscription,
  SubscriptionScheduledChange,
  UpdatedTermsOfService,
} = protos;

const DEFAULT_REQUEST_SWITCH_TO_BRAINTREE = {
  processorType: 'BRAINTREE',
  payoutConfig: {
    businessId: 0,
    purchaseConfigParameters: {
      configType: 'ROLLING_AFTER_SERVICE_DATE',
      rollingDays: 1,
    },
    orderConfigParameters: {
      configType: 'ROLLING_AFTER_SERVICE_DATE',
      rollingDays: 1,
    },
  },
};

export function archiveSubscription(subscription: any): ApiAction {
  return {
    TockApi: {
      getProto: `/business/${subscription.businessId}/payment/subscription/archive`,
      type: ClowncarAppConstants.SUBSCRIPTION_ARCHIVE,
      toast: `Archived subscription for ${subscription.businessName}`,
    },
  };
}

export function getActivationWarnings(business: core.IBusiness): ApiAction {
  return {
    TockApi: {
      getListProto: `/business/${business.id}/activate/warnings`,
      type: ClowncarAppConstants.ONBOARDING_ACTIVATION_WARNINGS,
    },
  };
}

export function activateBusiness(
  business: core.IBusiness,
  subscription: dashboard.ISubscription
): ApiAction {
  let queryParams = '';
  if (subscription.trialEndTimestampMs) {
    const trialEndString = LocalDate.ofInstant(
      Instant.ofEpochMilli(subscription.trialEndTimestampMs),
      ZoneId.of('America/Chicago')
    );
    queryParams = `?trialEndString=${trialEndString}`;
  }
  return {
    TockApi: {
      getProto: `/business/${business.id}/activate${queryParams}`,
      type: ClowncarAppConstants.ONBOARDING_ACTIVATE,
      toast: `${business.name} activated`,
    },
  };
}

export function deactivateBusiness(business: any): ApiAction {
  return {
    TockApi: {
      getProto: `/business/${business.id}/deactivate`,
      type: ClowncarAppConstants.ONBOARDING_DEACTIVATE,
      toast: `${business.name} deactivated`,
    },
  };
}

export function scheduleBusinessDeactivation(
  business: core.Business,
  scheduledDeactivationTimestampMs: number
) {
  return {
    TockApi: {
      putProto: `/internal/business/${business.id}/activation`,
      request: BusinessActivation.fromObject({
        scheduledDeactivationTimestampMs,
      }),
      type: ClowncarAppConstants.ONBOARDING_SCHEDULE_DEACTIVATION,
      toast: `${business.name} deactivation scheduled`,
    },
  };
}

export function cancelScheduledDeactivation(business: core.Business) {
  return {
    TockApi: {
      putProto: `/internal/business/${business.id}/activation`,
      request: BusinessActivation.fromObject({
        scheduledDeactivationTimestampMs: null,
      }),
      type: ClowncarAppConstants.ONBOARDING_CANCEL_SCHEDULE_DEACTIVATION,
      toast: `${business.name} scheduled deactivation canceled`,
    },
  };
}

export function deleteSubscription(subscription: any): ApiAction {
  const request = Subscription.fromObject({ note: subscription.note, addon: subscription.addon });
  return {
    TockApi: {
      postProto: `/business/${subscription.businessId}/payment/subscription/delete`,
      request,
      type: ClowncarAppConstants.SUBSCRIPTION_UPDATE,
      toast: 'Assigned free payment plan',
    },
  };
}

export function fetch(businessId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/internal/business/${businessId}`,
      type: ClowncarAppConstants.BUSINESS_FETCH,
    },
  };
}

export function fetchAll(): ApiAction {
  return {
    TockApi: {
      getListProto: '/business/all',
      type: ClowncarAppConstants.BUSINESSES_FETCH,
    },
  };
}

export function fetchAllByExportEnvironment(environment?: string): ApiAction {
  return {
    TockApi: {
      getListProto: `/business/all/environment?environment=${environment}`,
      type: ClowncarAppConstants.BUSINESSES_FETCH_EXPORT_ENVIRONMENT,
    },
  };
}

export function fetchAllByImportEnvironment(environment?: string): ApiAction {
  return {
    TockApi: {
      getListProto: `/business/all/environment?environment=${environment}`,
      type: ClowncarAppConstants.BUSINESSES_FETCH_IMPORT_ENVIRONMENT,
    },
  };
}

export function fetchBusinessGroups(): ApiAction {
  return {
    TockApi: {
      getListProto: '/internal/business/group',
      type: ClowncarAppConstants.BUSINESS_GROUPS_FETCH,
    },
  };
}

export function fetchBusinessGroup(businessGroupId: string): ApiAction {
  return {
    TockApi: {
      getProto: `/internal/business/group/${businessGroupId}`,
      type: ClowncarAppConstants.BUSINESS_GROUP_FETCH,
    },
  };
}

export function updateBusinessGroup(businessGroup: core.BusinessGroupInternal): ApiAction {
  const request = BusinessGroupInternal.fromObject(businessGroup);
  return {
    TockApi: {
      putProto: '/internal/business/group',
      request,
      toast: `${businessGroup.name} updated`,
      type: ClowncarAppConstants.BUSINESS_GROUP_UPDATE,
    },
  };
}

export function uploadBusinessGroupImage(businessGroupId: number, image: File): ApiAction {
  return {
    TockApi: {
      postFile: `/business/group/${businessGroupId}/image`,
      request: image,
      type: ClowncarAppConstants.BUSINESS_GROUP_UPDATE_IMAGE,
    },
  };
}

export function fetchSubscriptions(): ApiAction {
  return {
    TockApi: {
      getListProto: '/internal/business/payment',
      type: ClowncarAppConstants.SUBSCRIPTIONS_FETCH,
    },
  };
}

export function fetchSubscriptionPlans(
  currencyCode?: string,
  includeArchivedPlans?: boolean
): ApiAction {
  return {
    TockApi: {
      getListProto: `/business/payment/subscriptions?${stringify({
        currencyCode,
      })}&includeArchivedPlans=${includeArchivedPlans ?? false}`,
      type: ClowncarAppConstants.SUBSCRIPTION_PLANS_FETCH,
    },
  };
}

export function fetchSubscriptionPlansAddons(currencyCode?: string): ApiAction {
  return {
    TockApi: {
      getListProto: `/business/payment/subscriptions/addons?${stringify({ currencyCode })}`,
      type: ClowncarAppConstants.SUBSCRIPTION_PLAN_ADDONS_FETCH,
    },
  };
}

export function filterTags(filterValue: any): BaseTockAction {
  return {
    payload: filterValue,
    type: ClowncarAppConstants.BUSINESSES_SET_FILTER_GUEST_MARKETING,
  };
}

export function show(business: any): BaseTockAction {
  return {
    payload: business,
    type: ClowncarAppConstants.BUSINESS_SHOW,
  };
}

export function search(searchValue: any): BaseTockAction {
  return {
    payload: searchValue,
    type: ClowncarAppConstants.BUSINESSES_SEARCH_SET,
  };
}

export function updateBusiness(business: any): ApiAction {
  return {
    TockApi: {
      putProto: '/internal/business',
      type: ClowncarAppConstants.BUSINESSES_UPDATE_GUEST_MARKETING,
      request: business,
      scope: { businessId: safeToNumber(business.id) },
      toast: `${business.name} updated`,
    },
  };
}

export function updateSubscription(
  subscription: any,
  showToast = true,
  toastMessage?: string
): ApiAction {
  const subscriptionName = subscription.subscriptionName;
  const toastProps = showToast
    ? { toast: toastMessage || `Assigned ${subscriptionName} to ${subscription.businessName}` }
    : {};

  return {
    TockApi: {
      putProto: `/business/${subscription.businessId}/payment/subscription`,
      type: ClowncarAppConstants.SUBSCRIPTION_UPDATE,
      request: Subscription.fromObject(subscription),
      ...toastProps,
    },
  };
}

export function getSearchAttribute(businessId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/internal/business/search/attribute/${businessId}`,
      type: ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_FETCH,
    },
  };
}

export function insertSearchAttributes(businessId: number, values: any): ApiAction {
  const request = {
    ...values,
    businessId,
  };
  const searchAttribute = SearchAttribute.fromObject(request);
  return {
    TockApi: {
      postProto: `/internal/business/search/attribute`,
      request: searchAttribute,
      type: ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_INSERT,
      toast: 'Saved',
    },
  };
}

export function updateSearchAttributes(businessId: number, values: any): ApiAction {
  const request = {
    ...values,
    businessId,
  };
  const searchAttribute = SearchAttribute.fromObject(request);
  return {
    TockApi: {
      putProto: '/internal/business/search/attribute',
      request: searchAttribute,
      type: ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_UPDATE,
      toast: 'Saved',
    },
  };
}

export function updateBusinessType(businessId: number, businessType: number): ApiAction {
  const request = BusinessTypeRequest.fromObject(businessType);
  return {
    TockApi: {
      putProto: `/internal/business/type/${businessId}`,
      request,
      type: ClowncarAppConstants.BUSINESS_TYPE_UPDATE,
      toast: 'Saved',
    },
  };
}

export function updateInternalBusinessDetails(businessId: number, values: any): ApiAction {
  const request = BusinessDetailsUpdateRequest.fromObject(values);
  return {
    TockApi: {
      putProto: `/internal/business/details/${businessId}`,
      request,
      type: ClowncarAppConstants.INTERNAL_BUSINESS_DETAILS_UPDATE,
      toast: 'Saved',
    },
  };
}

export function updateBusinessTockProductTypeAndLevel(
  businessId: number,
  tockProductType: TockProductType,
  tockProductLevel: TockProductLevel
): ApiAction {
  const request = Business.fromObject({
    tockProductLevel,
    tockProductType,
  });
  return {
    TockApi: {
      putProto: `/business/${businessId}/product`,
      request,
      type: ClowncarAppConstants.BUSINESS_TOCK_PRODUCT_TYPE_LEVEL_UPDATE,
      toast: 'Saved Tock Product Type & Level',
    },
  };
}

export function setBusiness(business: core.Business): BaseTockAction {
  return {
    payload: business,
    type: ClowncarAppConstants.BUSINESS_SET,
  };
}

export function fetchTockBankAccount(businessId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/internal/business/${businessId}/bank/account`,
      type: ClowncarAppConstants.BANK_ACCOUNT_FETCH,
    },
  };
}

export function deleteBankAccount(businessId: number): ApiAction {
  const request = BankAccountUpdateRequest.fromObject({ deleteAccount: true });
  return {
    TockApi: {
      putProto: `/internal/business/${businessId}/bank/account`,
      request,
      type: ClowncarAppConstants.BANK_ACCOUNT_CHANGE,
      toast: 'Bank account deleted',
    },
  };
}

export function scheduleSubscriptionChange(
  businessId: number,
  scheduledChange: Partial<dashboard.SubscriptionScheduledChange>
): ApiAction {
  const request = SubscriptionScheduledChange.fromObject(scheduledChange);
  return {
    TockApi: {
      postProto: `/internal/business/${businessId}/payment/subscription/schedule`,
      request,
      type: ClowncarAppConstants.SUBSCRIPTION_SCHEDULE_CHANGE,
      toast: 'Subscription change scheduled',
    },
  };
}

export function removeSubscriptionScheduledChange(businessId: number): ApiAction {
  return {
    TockApi: {
      deleteProto: `/internal/business/${businessId}/payment/subscription/schedule`,
      type: ClowncarAppConstants.SUBSCRIPTION_SCHEDULED_CHANGE_REMOVED,
      toast: 'Subscription change removed',
    },
  };
}

export function changeDeactivatedSubscriptionEnd(
  businessId: number,
  endDate: LocalDate
): ApiAction {
  return {
    TockApi: {
      putProto: `/business/${businessId}/payment/subscription/deactivated?endDate=${endDate}`,
      type: ClowncarAppConstants.SUBSCRIPTION_DEACTIVATED_SUBSCRIPTION_END_CHANGED,
      toast: 'Subscription changed',
    },
  };
}

export function uploadCustomToS(request: dashboard.IUpdatedTermsOfService): ApiAction {
  return {
    TockApi: {
      postProto: `/internal/business/upload/tos`,
      request: UpdatedTermsOfService.fromObject(request),
      type: ClowncarAppConstants.CUSTOM_TOS_UPLOAD,
      toast: 'Custom terms of service uploaded',
    },
  };
}

export function clearToS(businessId: number) {
  return {
    TockApi: {
      deleteProto: `/internal/business/tos/${businessId}`,
      type: ClowncarAppConstants.CLEAR_TOS,
      toast: 'Terms of service cleared',
    },
  };
}

export function validatePhoneRequest(
  request: core.ValidatePhoneRequest
): ApiAction<core.ValidatePhoneResponse> {
  return {
    TockApi: {
      request: protos.ValidatePhoneRequest.fromObject(request),
      postProto: '/consumer/phone/validate',
      type: ClowncarAppConstants.VALIDATE_PHONE_REQUEST,
    },
  };
}

export function refundInvoice(request: dashboard.InvoiceChangeRequest): ApiAction {
  return {
    TockApi: {
      putProto: '/internal/invoices',
      request: InvoiceChangeRequest.fromObject(request),
      type: ClowncarAppConstants.INVOICE_REFUND,
      toast: 'Invoice refunded',
    },
  };
}

export function fetchSsoLoginToken(email: string): ApiAction<string> {
  return {
    TockApi: {
      getProto: `/internal/sso/login?email=${email}`,
      type: ClowncarAppConstants.SSO_LOGIN_TOKEN_FETCH,
    },
  };
}

export function updatePaymentProcessorToBraintree(businessId: number): ApiAction<void> {
  const request = cloneDeep(DEFAULT_REQUEST_SWITCH_TO_BRAINTREE);
  request.payoutConfig.businessId = businessId;
  return {
    TockApi: {
      putProto: '/internal/business/payouts',
      request: PayoutConfigChangeRequest.fromObject(request),
      type: ClowncarAppConstants.UPDATE_PAYMENT_PROCESSOR_TO_BRAINTREE,
    },
  };
}
